/**
 * A* over the tile grid, for the few things that need a real route rather than
 * a flow field.
 *
 * The horde uses flow fields — one Dijkstra sweep serves a hundred units headed
 * for the same place. Villagers cannot: each one is walking to a different
 * building, and there may be twenty such buildings. So they get A*, with the
 * results cached per destination so that every villager assigned to the same
 * woodcutter shares one route.
 *
 * This exists because of a bug worth remembering. Villagers steered directly at
 * their work site with no pathfinding at all, which was invisible until the
 * settlement had walls — and then villagers inside a wall ring simply pressed
 * against the stonework forever instead of walking out through their own gate.
 * Construction sites outside the wall were never finished, the population cap
 * never rose, and the whole economy quietly stopped. A player would hit this the
 * first time they enclosed their town.
 */

import { MAP_H, MAP_TILES, MAP_W } from './constants.ts';
import { alliedWith, TERRAIN_MOVE_COST, WallKind, Team, Terrain } from './types.ts';
import type { TileMap } from './map.ts';

const NX = [1, -1, 0, 0, 1, 1, -1, -1];
const NY = [0, 0, 1, -1, 1, -1, 1, -1];
const NCOST = [1, 1, 1, 1, 1.41421356, 1.41421356, 1.41421356, 1.41421356];

const OPEN = 1;
const CLOSED = 2;

/**
 * How much of the grid a single route may expand before giving up.
 *
 * A flat 4000 was a quarter of a 128×128 map and generous; on a 384×384 map it
 * is a fortieth, and a villager walking from one side of a real city to the
 * other would be told there is no route at all — which drops it onto the
 * straight-line fallback and walks it into its own stonework, the exact failure
 * this file exists to prevent. Scaled to the map, and capped so that an
 * genuinely unreachable goal still admits defeat quickly. Routes are memoised
 * per destination and only recomputed every nine hundred ticks, so the ceiling
 * is paid rarely.
 */
const DEFAULT_MAX_NODES = Math.min(48000, Math.max(4000, MAP_TILES >> 2));

/** Scratch state, reused between calls — A* is not re-entrant here. */
const gScore = new Float32Array(MAP_TILES);
const fScore = new Float32Array(MAP_TILES);
const cameFrom = new Int32Array(MAP_TILES);
const state = new Uint8Array(MAP_TILES);
/**
 * The A* frontier. Growable, because there is no decrease-key here either: a
 * tile is pushed again whenever a cheaper route to it turns up, so the number
 * of live entries is not bounded by the number of tiles. A typed array drops a
 * write past its end without complaining, which turns an overflow into silent
 * corruption of the search rather than a crash — see the note on the same bug
 * in `flow.ts`.
 */
let heapIdx = new Int32Array(MAP_TILES + 1);
let heapKey = new Float32Array(MAP_TILES + 1);
let heapSize = 0;

function growHeap(): void {
  const idx = new Int32Array(heapIdx.length * 2);
  const key = new Float32Array(heapKey.length * 2);
  idx.set(heapIdx);
  key.set(heapKey);
  heapIdx = idx;
  heapKey = key;
}
/** Bumped each search so the scratch arrays do not need clearing. */
const stamp = new Int32Array(MAP_TILES);
let epoch = 0;

function push(idx: number, key: number): void {
  if (heapSize + 1 >= heapIdx.length) growHeap();
  let i = ++heapSize;
  heapIdx[i] = idx;
  heapKey[i] = key;
  while (i > 1) {
    const p = i >> 1;
    if (heapKey[p] <= heapKey[i]) break;
    const ti = heapIdx[p];
    const tk = heapKey[p];
    heapIdx[p] = heapIdx[i];
    heapKey[p] = heapKey[i];
    heapIdx[i] = ti;
    heapKey[i] = tk;
    i = p;
  }
}

function pop(): number {
  const top = heapIdx[1];
  heapIdx[1] = heapIdx[heapSize];
  heapKey[1] = heapKey[heapSize];
  heapSize--;
  let i = 1;
  for (;;) {
    const l = i << 1;
    const r = l + 1;
    let m = i;
    if (l <= heapSize && heapKey[l] < heapKey[m]) m = l;
    if (r <= heapSize && heapKey[r] < heapKey[m]) m = r;
    if (m === i) break;
    const ti = heapIdx[m];
    const tk = heapKey[m];
    heapIdx[m] = heapIdx[i];
    heapKey[m] = heapKey[i];
    heapIdx[i] = ti;
    heapKey[i] = tk;
    i = m;
  }
  return top;
}

/**
 * Can `team` cross this tile? Gates open for the alliance that owns them.
 *
 * `wallTeam` is whose stonework this is — `Team.Player` in every settlement,
 * and the defender on an assault field. See `World.wallTeam`. `alliedWith`
 * rather than `===`, because a second banner fighting alongside the wall's
 * owner uses the same doors; identical for every team that existed before
 * there were four.
 */
function passable(map: TileMap, i: number, team: number, wallTeam: number): boolean {
  if (map.resourceSolid?.[i]) return false;
  const wall = map.wall[i];
  if (wall !== WallKind.None) {
    if (alliedWith(team, wallTeam)) return wall === WallKind.Gate;
    /**
     * A besieger's route does not stop at the enemy's wall — it prices it.
     *
     * This is the same rule `flow.ts` has always used and the reason the horde
     * works: a closed circuit that a search calls impassable is a search that
     * returns *no route at all*, and every caller of "no route" in this game
     * falls back to steering straight at the goal, which walks the army into
     * the stone and leaves it there. An impassable ring is the same shape of
     * fault as an impassable river, and it is answered the same way — the
     * route exists, it is expensive, and it therefore prefers the gate, the
     * breach or the thinnest section, in that order, without anything having
     * to go looking for a door.
     */
    return true;
  }
  if (map.building[i] >= 0) {
    // Same argument one layer in: your own roofs are geometry, theirs are
    // something to knock down on the way through.
    return !alliedWith(team, wallTeam);
  }
  /**
   * Rubble is walkable, at a price — and it is walkable everywhere else in the
   * simulation already.
   *
   * `TileMap.moveCost`, `TileMap.isImpassable`, `solid()` in the movement pass
   * and the flow fields all let a unit cross it. Only this function refused,
   * which meant the pathfinder was routing against a world that did not exist.
   * A siege that dropped a building across the one gate corridor stamped rubble
   * over it, `findPath` began returning "no route" for every work site outside
   * the ring, and `stepVillagers` fell through to straight-lining at the goal —
   * so villagers walked into their own stonework while perfectly able to step
   * over the rubble the whole time.
   *
   * The cost lives in the step weight below instead, which is where a
   * preference belongs. This is BALANCE.md pass 3 fault #2 arriving through a
   * door nobody had closed.
   */
  return true;
}

/**
 * What crossing rubble costs relative to clear ground.
 *
 * High enough that any reasonable detour wins, low enough that a route which
 * only exists across rubble is still found rather than reported impossible.
 */
const RUBBLE_COST = 3.5;

/**
 * What going *through* somebody else's stonework costs, in tile-steps.
 *
 * Deliberately the same numbers `flow.ts` charges the horde, so a company
 * marching on a town and a horde marching on a town make the same judgement
 * about the same wall: a full-health section is worth a long way round, a
 * battered one is worth considering, and a gate — which is a wall the defender
 * has to keep openable and is therefore the thinnest thing in the circuit — is
 * where a route lands when nothing is broken yet.
 */
const WALL_BASE_COST = 14;
const WALL_HP_COST = 0.055;
const BUILDING_BASE_COST = 22;

/** The extra a route pays for entering tile `i`, given whose walls these are. */
function structureCost(map: TileMap, i: number, team: number, wallTeam: number): number {
  if (alliedWith(team, wallTeam)) return 0;
  if (map.wall[i] !== WallKind.None) return WALL_BASE_COST + map.wallHp[i] * WALL_HP_COST;
  if (map.building[i] >= 0) return BUILDING_BASE_COST;
  return 0;
}

/**
 * Find a route from (sx, sy) to (tx, ty), written into `out` as tile indices
 * from start to goal. Returns the number of steps, or 0 if there is no route.
 *
 * `maxNodes` bounds the work: an unreachable goal on a 16k-tile map would
 * otherwise expand the whole grid before admitting defeat.
 */
export function findPath(
  map: TileMap,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  team: number,
  out: number[],
  maxNodes = DEFAULT_MAX_NODES,
  wallTeam: number = Team.Player,
  avoidWater = false,
): number {
  out.length = 0;
  if (sx < 0 || sy < 0 || sx >= MAP_W || sy >= MAP_H) return 0;
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return 0;

  const start = sy * MAP_W + sx;
  const goal = ty * MAP_W + tx;
  if (start === goal) return 0;
  const wet = (i: number) => avoidWater && (map.terrain[i] === Terrain.River || map.terrain[i] === Terrain.Deep || map.terrain[i] === Terrain.Ditch);
  if (wet(start) || wet(goal)) return 0;

  /**
   * Both ends of a villager's route are normally *inside* a building, and
   * `passable` rejects every tile a building stands on. Exempting only the
   * single goal tile was not enough for either end:
   *
   *   The start is the Hearth's centre, and the Hearth is 3×3 — so all eight
   *   neighbours of the start were rejected, the heap drained after one
   *   expansion, and every search returned "no route".
   *
   *   The goal is a work site's centre, and any footprint of 3×3 or more has
   *   the same problem in reverse: the goal's neighbours are all the same
   *   building, so it was unreachable even from a legal start.
   *
   * Between them, every route this cache ever computed came back empty and
   * every villager silently fell back to walking in a straight line — which is
   * exactly the bug this file's header says it exists to prevent. Letting a
   * route begin inside the building it starts in and end inside the one it is
   * headed for is what makes it work.
   */
  const startBuilding = map.building[start];
  const goalBuilding = map.building[goal];
  const exempt = (i: number): boolean =>
    i === goal ||
    (goalBuilding >= 0 && map.building[i] === goalBuilding) ||
    (startBuilding >= 0 && map.building[i] === startBuilding);

  epoch++;
  heapSize = 0;

  const h = (i: number) => {
    const dx = Math.abs((i % MAP_W) - tx);
    const dy = Math.abs(((i / MAP_W) | 0) - ty);
    // Octile distance — admissible for 8-way movement.
    return (dx + dy) + (1.41421356 - 2) * Math.min(dx, dy);
  };

  stamp[start] = epoch;
  gScore[start] = 0;
  fScore[start] = h(start);
  cameFrom[start] = -1;
  state[start] = OPEN;
  push(start, fScore[start]);

  let expanded = 0;
  while (heapSize > 0 && expanded < maxNodes) {
    const cur = pop();
    if (stamp[cur] !== epoch) continue;
    if (state[cur] === CLOSED) continue;
    state[cur] = CLOSED;
    expanded++;

    if (cur === goal) {
      let n = cur;
      const rev: number[] = [];
      while (n !== -1 && stamp[n] === epoch) {
        rev.push(n);
        n = cameFrom[n];
      }
      for (let i = rev.length - 1; i >= 0; i--) out.push(rev[i]);
      return out.length;
    }

    const cx = cur % MAP_W;
    const cy = (cur / MAP_W) | 0;
    for (let k = 0; k < 8; k++) {
      const nx = cx + NX[k];
      const ny = cy + NY[k];
      if (nx < 0 || ny < 0 || nx >= MAP_W || ny >= MAP_H) continue;
      const ni = ny * MAP_W + nx;
      // Tiles under the start's and the goal's own footprints are walkable for
      // this search; everything else has to be genuinely clear.
      if (map.resourceSolid?.[ni]) continue;
      if (wet(ni)) continue;
      if (!exempt(ni) && !passable(map, ni, team, wallTeam)) continue;
      if (k >= 4) {
        const sideX = cy * MAP_W + nx, sideY = ny * MAP_W + cx;
        if (map.resourceSolid?.[sideX] || map.resourceSolid?.[sideY]) continue;
        if (wet(sideX) || wet(sideY)) continue;
        // Pricing a hostile wall permits a route TO that stone for an attack;
        // it must not create a free diagonal passage past its corner.
        if ((map.wall[sideX] !== WallKind.None && !(map.wall[sideX] === WallKind.Gate && alliedWith(team, wallTeam)))
          || (map.wall[sideY] !== WallKind.None && !(map.wall[sideY] === WallKind.Gate && alliedWith(team, wallTeam)))) continue;
        if ((!exempt(sideX) && !passable(map, sideX, team, wallTeam)) || (!exempt(sideY) && !passable(map, sideY, team, wallTeam))) continue;
      }

      const step =
        NCOST[k] * (TERRAIN_MOVE_COST[map.terrain[ni]] ?? 1) * (map.rubble[ni] !== 0 ? RUBBLE_COST : 1) +
        structureCost(map, ni, team, wallTeam);
      const tentative = gScore[cur] + step;

      if (stamp[ni] !== epoch) {
        stamp[ni] = epoch;
        state[ni] = 0;
        gScore[ni] = Infinity;
      }
      if (state[ni] === CLOSED) continue;
      if (tentative < gScore[ni]) {
        cameFrom[ni] = cur;
        gScore[ni] = tentative;
        fScore[ni] = tentative + h(ni);
        state[ni] = OPEN;
        push(ni, fScore[ni]);
      }
    }
  }
  return 0;
}

/**
 * Routes to work sites, shared by every villager headed the same way and
 * rebuilt only when the world's shape changes.
 */
/**
 * Routes to work sites, memoised per destination.
 *
 * This is a *pure memo*, deliberately: whether a route is in the cache must
 * never change what the simulation does, only how long it takes to work it out.
 *
 * The first version had a per-tick recompute budget, which looked prudent and
 * quietly broke save/load. A freshly loaded world has an empty cache, so it
 * computed routes on different ticks than the world it was saved from, villagers
 * fell back to straight-line goals for a few ticks, and the two worlds diverged.
 * The cache is invalidated whenever the map's shape changes, so between
 * invalidations a route is constant — which means computing it eagerly costs a
 * little more and is worth far more.
 */
export class PathCache {
  private routes = new Map<number, number[]>();
  private builtAt = new Map<number, number>();

  /**
   * One Dijkstra from the settlement centre serves every route out of it.
   *
   * The cache used to run a separate A* per destination. That was fine when a
   * town had thirty buildings on a 128-tile map; on a 384-tile map with a few
   * thousand of them it was, by a wide margin, the most expensive thing in the
   * simulation — forty-three per cent of the tick on the profile — and it came
   * in *storms*, because `clear()` fires on every structural change and a siege
   * knocks buildings down constantly. Every wall that fell re-ran a couple of
   * thousand A* searches over a hundred and fifty thousand tiles.
   *
   * The standard answer is the one the horde already uses: when many routes
   * share an endpoint, sweep the grid once from that endpoint and read the
   * routes back off the predecessor field. Every route it yields is a shortest
   * path under exactly the weights `findPath` uses — same passability rule,
   * same terrain costs, same rubble penalty — so villagers walk the same
   * ground; only the arithmetic to find it is shared.
   *
   * `findPath` is kept, unchanged, for one-shot queries and for the tests that
   * pin the weights down.
   */
  private dist = new Float32Array(MAP_TILES);
  private prev = new Int32Array(MAP_TILES);
  /** The field being swept, published into `dist`/`prev` when it completes. */
  private sDist = new Float32Array(MAP_TILES);
  private sPrev = new Int32Array(MAP_TILES);
  /** Which tile the published field was swept from, or -1 for "none yet". */
  private fieldFrom = -1;
  /** Which tile the sweep in flight is from, or -1 when nothing is in flight. */
  private buildingFrom = -1;
  /** Set when the world's shape changed and the field is owed a rebuild. */
  private stale = true;
  /** Own heap, so a sliced sweep is not clobbered by a `findPath` in between. */
  private hIdx = new Int32Array(MAP_TILES + 1);
  private hKey = new Float32Array(MAP_TILES + 1);
  private hSize = 0;
  /**
   * Tiles already settled. Without it the sweep re-expands every tile that was
   * ever improved — on open ground that is a hundred times over — and the heap
   * grows past the number of tiles, which is exactly the overflow described in
   * `flow.ts`. The first pop of a tile is its final distance.
   */
  private readonly closed = new Uint8Array(MAP_TILES);

  /**
   * The world's shape changed.
   *
   * Deliberately *not* a wipe. The old routes stay valid to walk — they are
   * routes over ground that mostly still exists — and are handed out until the
   * new field has finished sweeping. Throwing them away instead meant every
   * villager in the town fell back to straight-lining at its work site for as
   * long as the rebuild took, which on a city-sized map is the exact failure
   * this file was written to prevent, arriving through the back door.
   *
   * `builtAt` is *not* reset either: the long refresh cadence is what re-reads
   * a route once the new field is in, and resetting it here would ask every
   * destination in the town to re-extract on the same tick.
   */
  clear(): void {
    this.stale = true;
  }

  /** Discard routing history when a terrain batch must publish atomically.
   * Buffers are reused; the next request builds the same field as a cold load. */
  reset(): void {
    this.routes.clear();
    this.builtAt.clear();
    this.fieldFrom = -1;
    this.buildingFrom = -1;
    this.hSize = 0;
    this.stale = true;
  }

  beginTick(): void {
    // Nothing to do; kept so the call site reads as a lifecycle.
  }

  /**
   * Push the sweep forward by at most `budget` tiles, starting one if the field
   * is owed a rebuild. Driven from `maybeRebuildFlows`, on the same budget and
   * for the same reason — see the header of `src/sim/flow.ts`.
   */
  advance(map: TileMap, budget: number): void {
    if (this.buildingFrom < 0) {
      if (!this.stale || this.fieldFrom < 0) return;
      this.beginField(map, this.fieldFrom);
    }
    this.sweep(map, budget);
  }

  /**
   * The route to a destination from the settlement centre. Keyed by destination,
   * because villagers all set out from roughly the same place.
   */
  get(map: TileMap, fromX: number, fromY: number, key: number, tx: number, ty: number, tick: number): number[] | null {
    const cached = this.routes.get(key);
    // Refresh on a long cadence so a route stays sane as the town fills in.
    const age = tick - (this.builtAt.get(key) ?? -1e9);
    if (cached && age < 900) return cached.length > 0 ? cached : null;

    const sx = Math.floor(fromX);
    const sy = Math.floor(fromY);
    const out: number[] = [];
    if (sx >= 0 && sy >= 0 && sx < MAP_W && sy < MAP_H && tx >= 0 && ty >= 0 && tx < MAP_W && ty < MAP_H) {
      this.ensureField(map, sy * MAP_W + sx);
      this.extract(map, sy * MAP_W + sx, ty * MAP_W + tx, out);
    }
    this.routes.set(key, out);
    this.builtAt.set(key, tick);
    return out.length > 0 ? out : null;
  }

  /**
   * Dijkstra from `start` over everything a villager can walk, recording a
   * predecessor per tile.
   *
   * Deliberately not A*: there is no single goal to aim at. The whole point is
   * that one sweep answers every destination in the town at once.
   */
  private ensureField(map: TileMap, start: number): void {
    // A sweep in flight from the same source: finish it now rather than hand
    // back a route from a field that does not exist yet.
    if (this.buildingFrom === start) {
      this.sweep(map, Number.POSITIVE_INFINITY);
      return;
    }
    if (this.fieldFrom === start && !this.stale) return;
    if (this.fieldFrom === start && this.stale) {
      // Stale but usable: a sliced rebuild is or will be under way, and the
      // published field still describes a world close enough to walk.
      return;
    }
    this.beginField(map, start);
    this.sweep(map, Number.POSITIVE_INFINITY);
  }

  private beginField(map: TileMap, start: number): void {
    this.buildingFrom = start;
    this.stale = false;

    const dist = this.sDist;
    const prev = this.sPrev;
    dist.fill(Infinity);
    prev.fill(-1);
    this.closed.fill(0);
    this.hSize = 0;

    /**
     * The route begins inside a building — the Hearth is 3×3 — and every tile
     * of that footprint is impassable to the general rule. Seeding the whole
     * footprint is `findPath`'s `exempt` for the start half, expressed once.
     */
    const startBuilding = map.building[start];
    dist[start] = 0;
    this.push(start, 0);
    if (startBuilding >= 0) {
      for (let i = 0; i < MAP_TILES; i++) {
        if (map.building[i] !== startBuilding || i === start) continue;
        dist[i] = 0;
        // Point the rest of the footprint at the tile the sweep is named for,
        // so a backtrack that ends inside the Hearth still terminates at
        // `start` rather than at a seed with no predecessor.
        prev[i] = start;
        this.push(i, 0);
      }
    }
  }

  /** Expand at most `budget` tiles of the sweep in flight. */
  private sweep(map: TileMap, budget: number): void {
    if (this.buildingFrom < 0) return;
    const dist = this.sDist;
    const prev = this.sPrev;
    let done = 0;

    const closed = this.closed;
    while (this.hSize > 0 && done < budget) {
      const cur = this.pop();
      if (closed[cur] === 1) continue;
      closed[cur] = 1;
      done++;
      const cd = dist[cur];
      const cx = cur % MAP_W;
      const cy = (cur / MAP_W) | 0;
      for (let k = 0; k < 8; k++) {
        const nx = cx + NX[k];
        const ny = cy + NY[k];
        if (nx < 0 || ny < 0 || nx >= MAP_W || ny >= MAP_H) continue;
        const ni = ny * MAP_W + nx;
        if (!passable(map, ni, Team.Player, Team.Player)) continue;
        const step =
          NCOST[k] * (TERRAIN_MOVE_COST[map.terrain[ni]] ?? 1) * (map.rubble[ni] !== 0 ? RUBBLE_COST : 1);
        const nd = cd + step;
        if (nd < dist[ni]) {
          dist[ni] = nd;
          prev[ni] = cur;
          this.push(ni, nd);
        }
      }
    }

    if (this.hSize > 0) return;
    // Finished: swap the new field in whole, and let every memoised route
    // re-extract off it on its own refresh cadence.
    const d = this.dist;
    const p2 = this.prev;
    this.dist = this.sDist;
    this.prev = this.sPrev;
    this.sDist = d;
    this.sPrev = p2;
    this.fieldFrom = this.buildingFrom;
    this.buildingFrom = -1;
    this.routes.clear();
  }

  private push(idx: number, key: number): void {
    if (this.hSize + 1 >= this.hIdx.length) {
      const idx2 = new Int32Array(this.hIdx.length * 2);
      const key2 = new Float32Array(this.hKey.length * 2);
      idx2.set(this.hIdx);
      key2.set(this.hKey);
      this.hIdx = idx2;
      this.hKey = key2;
    }
    let i = ++this.hSize;
    const hi = this.hIdx;
    const hk = this.hKey;
    while (i > 1) {
      const p = i >> 1;
      if (hk[p] <= key) break;
      hi[i] = hi[p];
      hk[i] = hk[p];
      i = p;
    }
    hi[i] = idx;
    hk[i] = key;
  }

  private pop(): number {
    const hi = this.hIdx;
    const hk = this.hKey;
    const top = hi[1];
    const lastIdx = hi[this.hSize];
    const lastKey = hk[this.hSize];
    this.hSize--;
    if (this.hSize > 0) {
      let i = 1;
      for (;;) {
        let m = i << 1;
        if (m > this.hSize) break;
        if (m + 1 <= this.hSize && hk[m + 1] < hk[m]) m++;
        if (hk[m] >= lastKey) break;
        hi[i] = hi[m];
        hk[i] = hk[m];
        i = m;
      }
      hi[i] = lastIdx;
      hk[i] = lastKey;
    }
    return top;
  }

  /**
   * Read a route back out of the field, start-to-goal.
   *
   * The goal is normally the centre of a building and therefore not reachable
   * itself — nothing may stand on a building tile. So the walk begins at the
   * cheapest tile that actually touches the destination's footprint, and the
   * destination is appended, which is the same shape of route `findPath`
   * returned when it exempted the goal building's own tiles.
   */
  private extract(map: TileMap, start: number, goal: number, out: number[]): void {
    if (start === goal) return;
    const dist = this.dist;

    let entry = -1;
    let entryD = Infinity;
    if (dist[goal] < Infinity) {
      entry = goal;
      entryD = dist[goal];
    } else {
      // Ring of tiles around the goal's footprint, or around the goal itself.
      const gb = map.building[goal];
      const gx = goal % MAP_W;
      const gy = (goal / MAP_W) | 0;
      let x0 = gx;
      let y0 = gy;
      let x1 = gx;
      let y1 = gy;
      if (gb >= 0) {
        // Footprints are small rectangles; widen from the goal until the
        // building stops, which is cheaper than scanning the store.
        while (x0 > 0 && map.building[gy * MAP_W + (x0 - 1)] === gb) x0--;
        while (x1 < MAP_W - 1 && map.building[gy * MAP_W + (x1 + 1)] === gb) x1++;
        while (y0 > 0 && map.building[(y0 - 1) * MAP_W + gx] === gb) y0--;
        while (y1 < MAP_H - 1 && map.building[(y1 + 1) * MAP_W + gx] === gb) y1++;
      }
      for (let y = y0 - 1; y <= y1 + 1; y++) {
        if (y < 0 || y >= MAP_H) continue;
        for (let x = x0 - 1; x <= x1 + 1; x++) {
          if (x < 0 || x >= MAP_W) continue;
          const i = y * MAP_W + x;
          // Lowest cost wins; on a tie the lowest tile index, which is the
          // scan order, so the answer is a pure function of the field.
          if (dist[i] < entryD) {
            entryD = dist[i];
            entry = i;
          }
        }
      }
    }
    if (entry < 0 || entryD === Infinity) return;

    const rev: number[] = [];
    let n = entry;
    let guard = 0;
    while (n !== -1 && guard++ <= MAP_TILES) {
      rev.push(n);
      if (n === start) break;
      n = this.prev[n];
    }
    if (rev.length === 0 || rev[rev.length - 1] !== start) return;
    for (let i = rev.length - 1; i >= 0; i--) out.push(rev[i]);
    if (entry !== goal) out.push(goal);
  }
}
